import { z } from 'zod';

import { IdSchema, StringSchema } from '../../base/primitives';
import {
  FunctionArchivedSchema,
  FunctionCodeSchema,
  FunctionErrorSchema,
  FunctionNameSchema,
  FunctionParameterSchema,
} from '../functions';
import { SpecialFunctionReferencesSchema } from './special-function.schema';

// ============================================================================
// Base Command Schema Components
// ============================================================================

// Base schema for special function properties used in commands
const SpecialFunctionBaseCommandSchema = z.object({
  name: FunctionNameSchema,
  description: StringSchema.optional().default(''),
  code: FunctionCodeSchema,
  parameters: z.array(FunctionParameterSchema).optional().default([]),
  archived: FunctionArchivedSchema.default(false),
  errors: z.array(FunctionErrorSchema),
  references: SpecialFunctionReferencesSchema.optional(),
});

const BaseEntityCommandSchema = z.object({
  id: IdSchema,
});

// Errors update schema for update commands
const ErrorsUpdateSchema = z.object({
  errors: z.array(FunctionErrorSchema),
});

// ============================================================================
// Special Functions Command Schemas
// ============================================================================

export const CreateStartFunctionCommandSchema = BaseEntityCommandSchema.merge(
  SpecialFunctionBaseCommandSchema,
)
  .extend({
    archived: FunctionArchivedSchema.default(false),
  })
  .describe('Command to create a new start function');

export const CreateEndFunctionCommandSchema = BaseEntityCommandSchema.merge(
  SpecialFunctionBaseCommandSchema,
)
  .extend({
    archived: FunctionArchivedSchema.default(false),
  })
  .describe('Command to create a new end function');

export const UpdateStartFunctionCommandSchema = BaseEntityCommandSchema.merge(
  z.object({
    description: StringSchema.optional().default(''),
    code: FunctionCodeSchema,
    archived: FunctionArchivedSchema.optional().default(false),
    errors: ErrorsUpdateSchema.optional(),
    references: SpecialFunctionReferencesSchema.optional(),
  }),
).describe('Command to update an existing start function');

export const UpdateEndFunctionCommandSchema =
  UpdateStartFunctionCommandSchema.describe(
    'Command to update an existing end function',
  );

// ============================================================================
// Type Exports
// ============================================================================

export type CreateStartFunctionCommand = z.infer<
  typeof CreateStartFunctionCommandSchema
>;
export type CreateEndFunctionCommand = z.infer<
  typeof CreateEndFunctionCommandSchema
>;
export type UpdateStartFunctionCommand = z.infer<
  typeof UpdateStartFunctionCommandSchema
>;
export type UpdateEndFunctionCommand = z.infer<
  typeof UpdateEndFunctionCommandSchema
>;
